import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { ArrowRight, FileText, Mic, Upload } from "lucide-react";

export default function Landing() {
  const { t, lang } = useLang();
  const phases = [t("phase_1"), t("phase_2"), t("phase_3"), t("phase_4"), t("phase_5")];
  const disciplines = [
    { t: t("disc_1_t"), d: t("disc_1_d") },
    { t: t("disc_2_t"), d: t("disc_2_d") },
    { t: t("disc_3_t"), d: t("disc_3_d") },
    { t: t("disc_4_t"), d: t("disc_4_d") },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]" data-testid="landing-page">
      <Header />

      {/* Marquee strip */}
      <div className="bg-[#0A0A0A] text-white overflow-hidden border-b border-[#0A0A0A]">
        <div className="flex whitespace-nowrap marquee-track py-2 font-mono-tech text-xs uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0">
              {[
                "FASE 01 · RADIOGRAFÍA",
                "FASE 02 · MECANISMOS RETÓRICOS",
                "FASE 03 · FRAMING COGNITIVO",
                "FASE 04 · ESTRATEGIA PROFUNDA",
                "FASE 05 · MAPA ESTRATÉGICO",
                "🔬 GALASCIODAS™",
              ].map((s, i) => (
                <span key={i} className="px-8 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[#D90429]" />
                  {s}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="border-b border-[#171717]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-8">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-8 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D90429]" />
              {t("hero_eyebrow")}
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter font-bold mb-8 whitespace-pre-line">
              {t("hero_title")}
            </h1>
            <p className="text-lg md:text-xl text-[#404040] max-w-2xl leading-relaxed mb-10">
              {t("hero_lead")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="hero-cta-start"
                className="group bg-[#0A0A0A] text-white px-8 py-4 font-mono-tech text-xs uppercase tracking-[0.25em] border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] transition-colors inline-flex items-center gap-3"
              >
                {t("hero_cta_start")}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/docs"
                data-testid="hero-cta-learn"
                className="px-8 py-4 font-mono-tech text-xs uppercase tracking-[0.25em] border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {t("hero_cta_learn")}
              </Link>
            </div>
          </div>

          <aside className="md:col-span-4 border border-[#171717] bg-white p-6 md:p-8 self-start">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] mb-4">
              · MUESTRA · ANÁLISIS
            </div>
            <div className="font-serif-display italic text-xl leading-snug text-[#0A0A0A] mb-6">
              “No estamos solos. Detrás de cada decisión, los enemigos del pueblo trabajan contra nosotros.”
            </div>
            <div className="space-y-3 font-mono-tech text-[11px]">
              <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                <span className="text-[#A3A3A3] uppercase">Mecanismo</span>
                <span>NOSOTROS vs ELLOS</span>
              </div>
              <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                <span className="text-[#A3A3A3] uppercase">Apelación</span>
                <span>MIEDO + ORGULLO</span>
              </div>
              <div className="flex justify-between border-b border-[#E5E5E5] pb-2">
                <span className="text-[#A3A3A3] uppercase">Objetivo</span>
                <span>CONSOLIDAR BASE</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A3A3A3] uppercase">Sofisticación</span>
                <span className="text-[#D90429]">█ █ █ ░</span>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Disciplines */}
      <section className="border-b border-[#171717]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-baseline justify-between mb-12 flex-wrap gap-4">
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight">
              {t("disciplines_title")}
            </h2>
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">
              · 04 PILARES
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-[#171717] border border-[#171717]">
            {disciplines.map((d, i) => (
              <div key={i} className="bg-white p-8 md:p-10">
                <div className="font-mono-tech text-xs tracking-[0.3em] text-[#D90429] mb-4">
                  0{i + 1}
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl font-bold mb-3 leading-tight">
                  {d.t}
                </h3>
                <p className="text-[#404040] leading-relaxed">{d.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protocol */}
      <section id="protocol" className="border-b border-[#171717] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-6">
                · PROTOCOLO
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight leading-none mb-6">
                {t("phases_title")}
              </h2>
              <p className="text-[#404040] leading-relaxed mb-8">
                {lang === "es"
                  ? "Cada fragmento de discurso atraviesa cinco fases secuenciales. El resultado: un mapa estratégico que revela qué quiere conseguir realmente, a quién le habla, y qué oculta entre líneas."
                  : "Each discourse fragment goes through five sequential phases. The result: a strategic map revealing what they really want to achieve, who they are speaking to, and what they hide between the lines."}
              </p>
              <Link
                to="/register"
                data-testid="protocol-cta"
                className="inline-flex items-center gap-3 font-mono-tech text-xs uppercase tracking-[0.25em] border-b border-[#0A0A0A] pb-1 hover:text-[#D90429] hover:border-[#D90429]"
              >
                {t("hero_cta_start")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <ol className="md:col-span-8 space-y-0 border-t border-[#171717]">
              {phases.map((p, i) => (
                <li
                  key={i}
                  className="grid grid-cols-[60px_1fr] md:grid-cols-[100px_1fr_auto] gap-4 md:gap-8 items-baseline border-b border-[#171717] py-6 group hover:bg-[#FAFAFA] transition-colors"
                >
                  <span className="font-mono-tech text-sm md:text-base font-bold">
                    FASE {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif-display text-xl md:text-3xl tracking-tight">
                    {p}
                  </span>
                  <span className="hidden md:inline-block font-mono-tech text-xs text-[#A3A3A3] group-hover:text-[#D90429]">
                    →
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Input methods */}
      <section className="border-b border-[#171717]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-3 gap-px bg-[#171717] border border-[#171717]">
          {[
            { I: FileText, label: lang === "es" ? "TEXTO" : "TEXT", d: lang === "es" ? "Discursos, declaraciones, tweets, entrevistas." : "Speeches, statements, tweets, interviews." },
            { I: Upload, label: lang === "es" ? "ARCHIVO" : "FILE", d: lang === "es" ? "Sube transcripciones .txt o .pdf." : "Upload .txt or .pdf transcripts." },
            { I: Mic, label: "AUDIO", d: lang === "es" ? "Transcribe automáticamente con Whisper." : "Auto-transcribed with Whisper." },
          ].map((it, i) => (
            <div key={i} className="bg-white p-10 flex flex-col">
              <it.I className="w-8 h-8 mb-6 stroke-[1.5]" />
              <div className="font-mono-tech text-xs tracking-[0.3em] mb-2">· 0{i + 1}</div>
              <div className="font-serif-display text-3xl font-bold mb-3">{it.label}</div>
              <p className="text-[#404040]">{it.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-serif-display text-2xl font-bold">GalascioDAS™</div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase opacity-60 mt-1">
              {t("footer_signature")}
            </div>
          </div>
          <Link
            to="/register"
            className="px-6 py-3 border border-white font-mono-tech text-xs uppercase tracking-[0.25em] hover:bg-[#D90429] hover:border-[#D90429]"
          >
            {t("hero_cta_start")} →
          </Link>
        </div>
      </footer>
    </div>
  );
}
